import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {DatePickerInput} from '@mantine/dates';
import {IconThumbDownFilled} from '@tabler/icons-react';
import type {MRT_ColumnDef} from 'mantine-react-table';
import {useMemo, useState} from 'react';
import type {AssistantConversationSummaryResponse} from 'src/api/ProactApiClient';
import {SelectBox} from 'src/design/atoms/SelectBox';
import {Switch} from 'src/design/atoms/Switch';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {
  useAssistantConversationsQuery,
  useGlobalAssistantConversationsQuery,
} from 'src/redux/queries/assistantControlCenter';
import {formatUtcDateTime} from 'src/shared/utils/formatters';
import type {ISelectOption} from 'src/types/Form';
import {formatTokens, formatUsd} from './assistantUsageFormat';
import {toUtcDayRange} from 'src/shared/utils/utcDate';

export interface AssistantConversationsTableProps {
  scope: 'instance' | 'global';
  /** Global page instance filter — narrows conversations to one instance. */
  instanceId?: number;
  userOptions: ISelectOption[];
  showInstanceColumn?: boolean;
  showCost?: boolean;
  onViewTranscript: (id: number) => void;
}

export const AssistantConversationsTable = ({
  scope,
  instanceId,
  userOptions,
  showInstanceColumn,
  showCost = true,
  onViewTranscript,
}: AssistantConversationsTableProps) => {
  const columns = useMemo<MRT_ColumnDef<AssistantConversationSummaryResponse>[]>(
    () => [
      {
        accessorKey: 'userName',
        header: 'User',
        Cell: ({cell}) => cell.getValue<string>() ?? '—',
      },
      ...(showInstanceColumn
        ? [
            {
              accessorKey: 'instanceName',
              header: 'Instance',
              Cell: ({cell}) => cell.getValue<string>() ?? '—',
            } as MRT_ColumnDef<AssistantConversationSummaryResponse>,
          ]
        : []),
      {
        accessorKey: 'createdAt',
        header: 'Created',
        Cell: ({row}) => formatUtcDateTime(row.original.createdAt),
      },
      {
        accessorKey: 'lastActivityAt',
        header: 'Last Activity',
        Cell: ({row}) => formatUtcDateTime(row.original.lastActivityAt),
      },
      {
        accessorKey: 'messageCount',
        header: 'Messages',
      },
      {
        accessorKey: 'negativeFeedbackCount',
        header: 'Negative Feedback',
        Cell: ({row}) => {
          const count = row.original.negativeFeedbackCount;
          return count > 0 ? (
            <Group gap={4} wrap="nowrap">
              <Box c="red.7" style={{display: 'flex'}}>
                <IconThumbDownFilled size={14} aria-hidden />
              </Box>
              <Text size="sm">{count}</Text>
            </Group>
          ) : null;
        },
      },
      {
        id: 'tokens',
        header: 'Tokens',
        Cell: ({row}) =>
          formatTokens(row.original.tokens?.totalTokens ?? undefined),
      },
      ...(showCost
        ? [
            {
              id: 'estimatedCostUsd',
              header: 'Est. Cost',
              Cell: ({row}) =>
                formatUsd(row.original.tokens?.estimatedCostUsd ?? undefined),
            } as MRT_ColumnDef<AssistantConversationSummaryResponse>,
          ]
        : []),
    ],
    [showInstanceColumn, showCost]
  );

  const tableState = useTableState({
    identifier: `assistant-conversations-${scope}`,
    columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 25,
    },
  });

  const {start, length, setPagination} = tableState;

  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [negativeOnly, setNegativeOnly] = useState(false);

  const resetToFirstPage = () =>
    setPagination(prev => ({...prev, pageIndex: 0}));

  const request = {
    start,
    length,
    instanceId,
    userId,
    hasNegativeFeedback: negativeOnly || undefined,
    ...toUtcDayRange(dateRange),
  };

  const instanceResult = useAssistantConversationsQuery(request, {
    skip: scope !== 'instance',
  });
  const globalResult = useGlobalAssistantConversationsQuery(request, {
    skip: scope !== 'global',
  });

  const {data, isLoading, isFetching, isError, refetch} =
    scope === 'instance' ? instanceResult : globalResult;

  const rows = data?.data ?? [];
  const totalCount = data?.filteredRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualPagination: true,
      manualSorting: true,
      manualFiltering: true,
      enableSorting: false,
      enableColumnFilters: false,
      columns,
      data: rows,
      getRowId: row => String(row.id),
      renderEmptyRowsFallback: () => (
        <Text c="dimmed" ta="center" py="xl" size="sm">
          No conversations match these filters.
        </Text>
      ),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            tooltip="View transcript"
            onClick={() => onViewTranscript(row.original.id)}
          />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Filters>
            <Box w={220}>
              <SelectBox
                placeholder="All users"
                ariaLabel="Filter by user"
                options={userOptions}
                value={userId !== undefined ? String(userId) : undefined}
                onSelectChangeCallback={val => {
                  setUserId(val ? Number(val) : undefined);
                  resetToFirstPage();
                }}
                wrapperProps={{mb: 0}}
              />
            </Box>
            <DatePickerInput
              type="range"
              placeholder="All dates"
              aria-label="Filter by date range"
              clearable
              value={dateRange}
              onChange={next => {
                setDateRange(next);
                resetToFirstPage();
              }}
              w={240}
            />
            <Switch
              label="Has negative feedback"
              checked={negativeOnly}
              onChange={event => {
                setNegativeOnly(event.currentTarget.checked);
                resetToFirstPage();
              }}
            />
          </TableToolbar.Filters>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return table;
};
